import { get, find } from 'lodash';
import BaseService from '@core/common/BaseService';
import { ReportingClient, RESOURCES as REPORTING_RESOURCES } from '@core/clients/ReportingClient';
import { AgentManagerClient, RESOURCES as AGENT_MANAGER_RESOURCES } from '@core/clients/AgentManagerClient';
import { Subscription } from '@core/common/Subscription';
import { SOURCE_CONTEXT_NAMES, WIDGET_LOGICAL_NAMES } from '@config/Consts';
import botsListTransformer from './botsListTransformer';

const cancellationTokens = {};

function getEligibleResources() {
  return {
    MESSAGING_CSAT_DISTRIBUTIONS: REPORTING_RESOURCES.MESSAGING_CSAT_DISTRIBUTIONS,
    AGENT_VIEW: AGENT_MANAGER_RESOURCES.AGENT_VIEW,
    METRICS: AGENT_MANAGER_RESOURCES.METRICS,
    HISTORICAL: AGENT_MANAGER_RESOURCES.HISTORICAL,
  };
}

function createBotsListClients(params) {
  const contextName = SOURCE_CONTEXT_NAMES[WIDGET_LOGICAL_NAMES.BOTS];

  const messagingCsatDistributionClient = new ReportingClient({
    resource: REPORTING_RESOURCES.MESSAGING_CSAT_DISTRIBUTIONS,
    contextName,
  });
  messagingCsatDistributionClient.useRequestInterceptor(params.interceptor);

  const agentViewClient = new AgentManagerClient({
    resource: AGENT_MANAGER_RESOURCES.AGENT_VIEW,
    contextName,
  });
  agentViewClient.useRequestInterceptor(params.interceptor);

  const metricsClient = new AgentManagerClient({
    resource: AGENT_MANAGER_RESOURCES.METRICS,
    contextName,
  });
  metricsClient.useRequestInterceptor(params.interceptor);

  const historicalClient = new AgentManagerClient({
    resource: AGENT_MANAGER_RESOURCES.HISTORICAL,
    contextName,
  });
  historicalClient.useRequestInterceptor(params.interceptor);

  const RESOURCES_TO_CLIENT_MAP = {
    [REPORTING_RESOURCES.MESSAGING_CSAT_DISTRIBUTIONS]: messagingCsatDistributionClient,
    [AGENT_MANAGER_RESOURCES.AGENT_VIEW]: agentViewClient,
    [AGENT_MANAGER_RESOURCES.METRICS]: metricsClient,
    [AGENT_MANAGER_RESOURCES.HISTORICAL]: historicalClient,
  };

  const getClient = resourceName => RESOURCES_TO_CLIENT_MAP[resourceName];

  return {
    getClient,
  };
}

function stopListenToBotsListClientEvents(resourceName) {
  const client = this.botsListClients.getClient(resourceName);
  client.clearAllListeners(client.constructor.EVENTS.onPollingSuccess);
  client.clearAllListeners(client.constructor.EVENTS.onPollingError);
}

async function enrichBotsListData(resourceName, botsListResponse) {
  const enrichedBotsList = botsListTransformer.transformResponse(resourceName, botsListResponse);

  const resourceSubscription = this.subscriptions.find(
    subscriptionRecord => subscriptionRecord.name === resourceName);
  resourceSubscription.subscription.emit(Subscription.EVENTS.onSubscriptionUpdateNotification,
    { name: resourceName, data: enrichedBotsList });
}

function onBotsListSubscriptionError(resourceName, error) {
  const resourceSubscription = this.subscriptions.find(
    subscriptionRecord => subscriptionRecord.name === resourceName);
  resourceSubscription.subscription.emit(Subscription.EVENTS.onSubscriptionError, error);
}

function listenToBotsListClientEvents(settings, resourceName) {
  stopListenToBotsListClientEvents.call(this, resourceName);
  const client = this.botsListClients.getClient(resourceName);
  const clientEvents = client.constructor.EVENTS;
  client.on(
    clientEvents.onPollingSuccess,
    enrichBotsListData.bind(this, resourceName),
    'BotListServicePollingSuccess');
  client.on(
    clientEvents.onPollingError, onBotsListSubscriptionError.bind(this, resourceName),
    'BotListServicePollingError');
}

function botsSubscriptionStartMethod(settings) {
  const request = botsListTransformer.transformRequest(settings.name, settings.settings);

  listenToBotsListClientEvents.call(this, settings.settings, settings.name);
  cancellationTokens[settings.name] = this.botsListClients.getClient(settings.name)
    .subscribe(request);
}

function botsSubscriptionStopMethod(settings) {
  const token = get(cancellationTokens, settings.name);
  if (token) {
    stopListenToBotsListClientEvents.call(this, settings.name);
    this.botsListClients.getClient(settings.name).unsubscribe(token);
  }
}

/**
 * Bots List service -
 * Requests data 3 different APIs:
 * Agent manager- Agent-view (bots data)
 * Agent manager- Metrics (bots status summary)
 * Agent manager- Historical (bots graph)
 * Messaging CSAT Distribution
 */
class BotsListService extends BaseService {
  constructor() {
    super({ name: 'BotsListService' });
  }

  async getBotsListSubscriptions(params) {
    if (!this.subscriptions) {
      this.botsListClients = createBotsListClients(params);
      this.subscriptions = [];
      const resources = getEligibleResources();
      Object.keys(resources).forEach((key) => {
        const resourceName = resources[key];
        const resourceSubscription = new Subscription({
          name: `${resourceName}`,
          resourceName,
          handlers: {
            start: botsSubscriptionStartMethod.bind(this),
            stop: botsSubscriptionStopMethod.bind(this),
          },
        });
        this.subscriptions.push({ name: resourceName, subscription: resourceSubscription });
      });
      return { subscriptions: this.subscriptions };
    }
    return { subscriptions: this.subscriptions };
  }

  async getBotsGraphSubscription() {
    if (!this.graphSubscription) {
      const resourceName = AGENT_MANAGER_RESOURCES.HISTORICAL;
      this.graphSubscription = find(this.subscriptions, subscription =>
        subscription.name === resourceName);
    }
    return this.graphSubscription;
  }
}

const botsListService = new BotsListService();

export default botsListService;
